/**
 * platform-promo-page.tsx — Platform → Mã ưu đãi (super-admin). Route `/platform/promo-codes`.
 *
 * Tạo / sửa / bật-tắt / xoá mã giảm giá cho gói dịch vụ. Khách nhập mã ở trang đổi gói
 * (hoặc qua link `/billing/upgrade?promo=MA`). Mã đã có người dùng thì "Xoá" chỉ tắt mã.
 * Backend: `billing/promo-admin-routes.ts`; điều kiện áp mã: `billing/pricing.ts#applyPromo`.
 */
import { useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { Copy, Loader2, Pencil, Plus, Search, Shuffle, Trash2, Users } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { ErrorState, Loading, EmptyState } from '@/components/shared/feedback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox, Switch, Textarea } from '@/components/ui/misc'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { platformApiError } from '@/lib/platform-client'
import { formatVnd } from '@/lib/format-money'
import { cn } from '@/lib/utils'
import { usePlatformPricing } from '@/hooks/use-platform-pricing'
import {
  generatePromoCode, useDeletePromoCode, usePromoCodes, usePromoRedemptions, useSavePromoCode, useTogglePromoCode,
} from '@/hooks/use-platform-promo'
import { formatIntInput, parseIntInput } from './pricing-editor'
import {
  discountLabel, EMPTY_PROMO_FORM, formToInput, promoStatus, PROMO_STATUS_LABEL, promoToForm, validatePromoForm,
  type PromoCodeItem, type PromoForm,
} from './promo-editor'

const fmtDate = (iso: string | null) => (iso ? dayjs(iso).format('DD/MM/YYYY HH:mm') : null)

/** Mã phạm vi "Credit AI" trong promo_codes.plan_ids (xem docs/AI-2-CHE-DO-HOP-DONG-API.md). */
const AI_CREDITS_SCOPE = 'ai_credits'

export function PlatformPromoPage() {
  const { data, isLoading, isError } = usePromoCodes()
  const pricing = usePlatformPricing()
  const toggle = useTogglePromoCode()
  const del = useDeletePromoCode()
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState<PromoCodeItem | 'new' | null>(null)
  const [viewing, setViewing] = useState<PromoCodeItem | null>(null)

  // Phạm vi "Credit AI" (AI hai chế độ): mã chỉ áp cho mua credit khi planIds chứa 'ai_credits'.
  const plans = useMemo(
    () => [
      ...(pricing.data?.pricing.plans ?? []).filter((p) => p.purchasable).map((p) => ({ id: p.id, name: p.name })),
      { id: AI_CREDITS_SCOPE, name: 'Token AI' },
    ],
    [pricing.data],
  )
  const planName = (id: string) => (id === AI_CREDITS_SCOPE ? 'Token AI' : pricing.data?.pricing.plans.find((p) => p.id === id)?.name ?? id)

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase()
    const all = data ?? []
    return s ? all.filter((p) => p.code.toLowerCase().includes(s) || p.description.toLowerCase().includes(s)) : all
  }, [data, q])

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      toast.success(`Đã sao chép ${code}`)
    } catch {
      toast.error('Không sao chép được')
    }
  }

  const remove = (p: PromoCodeItem) => {
    const msg = p.usedCount > 0
      ? `Mã ${p.code} đã có ${p.usedCount} lượt dùng nên không xoá được — mã sẽ bị TẮT. Tiếp tục?`
      : `Xoá mã ${p.code}? Không hoàn tác được.`
    if (!window.confirm(msg)) return
    del.mutate(p.id, {
      onSuccess: (r) => toast.success(r.deleted ? `Đã xoá mã ${p.code}` : `Đã tắt mã ${p.code}`),
      onError: (err) => toast.error(platformApiError(err)),
    })
  }

  const columns: Column<PromoCodeItem>[] = [
    {
      key: 'code', header: 'Mã',
      cell: (p) => (
        <div className="min-w-0 max-w-[260px]">
          <button type="button" className="inline-flex items-center gap-1.5 font-mono font-medium hover:text-primary" onClick={() => copy(p.code)} title="Sao chép mã">
            {p.code} <Copy className="h-3 w-3 opacity-60" />
          </button>
          {p.description && <p className="truncate text-xs text-muted-foreground" title={p.description}>{p.description}</p>}
        </div>
      ),
    },
    {
      key: 'discount', header: 'Giảm',
      cell: (p) => (
        <div className="whitespace-nowrap text-sm">
          <p className="font-medium">{discountLabel(p)}</p>
          <p className="text-xs text-muted-foreground">
            {p.stackableWithDurationDiscount ? 'Cộng dồn giảm thời hạn' : 'Không cộng dồn'}
          </p>
        </div>
      ),
    },
    {
      key: 'plans', header: 'Gói áp dụng',
      cell: (p) => (
        <div className="flex max-w-[220px] flex-wrap gap-1">
          {p.planIds.length === 0 ? <span className="text-sm text-muted-foreground">Mọi gói trả phí</span>
            : p.planIds.map((id) => <Badge key={id} variant="default">{planName(id)}</Badge>)}
          {(p.minDays != null || p.minAmount != null || p.firstPurchaseOnly) && (
            <p className="w-full text-xs text-muted-foreground">
              {[p.minDays != null && `≥ ${p.minDays} ngày`, p.minAmount != null && `đơn ≥ ${formatIntInput(p.minAmount)}đ`, p.firstPurchaseOnly && 'lần mua đầu']
                .filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'window', header: 'Hiệu lực',
      cell: (p) => (
        <div className="whitespace-nowrap text-xs text-muted-foreground">
          {!p.startsAt && !p.endsAt ? 'Không giới hạn' : (
            <>
              <p>Từ {fmtDate(p.startsAt) ?? '—'}</p>
              <p>Đến {fmtDate(p.endsAt) ?? '—'}</p>
            </>
          )}
        </div>
      ),
    },
    {
      key: 'used', header: 'Đã dùng', align: 'right',
      cell: (p) => (
        <button type="button" className="whitespace-nowrap text-right text-sm tabular-nums hover:text-primary" onClick={() => setViewing(p)} title="Xem lượt dùng">
          <span className="font-medium">{p.usedCount}</span>
          <span className="text-muted-foreground"> / {p.maxRedemptions ?? '∞'}</span>
          <p className="text-xs text-muted-foreground">{p.maxPerOrg} lượt / tổ chức</p>
        </button>
      ),
    },
    {
      key: 'status', header: 'Trạng thái',
      cell: (p) => {
        const s = PROMO_STATUS_LABEL[promoStatus(p)]
        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={p.isActive}
              disabled={toggle.isPending && toggle.variables?.id === p.id}
              onCheckedChange={(v) => toggle.mutate({ id: p.id, isActive: v }, { onError: (err) => toast.error(platformApiError(err)) })}
              aria-label={p.isActive ? 'Tắt mã' : 'Bật mã'}
            />
            <Badge variant={s.variant}>{s.text}</Badge>
          </div>
        )
      },
    },
    {
      key: 'actions', header: '', align: 'right',
      cell: (p) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => setViewing(p)} title="Lượt dùng"><Users className="h-3.5 w-3.5" /></Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(p)} title="Sửa"><Pencil className="h-3.5 w-3.5" /></Button>
          <Button size="sm" variant="ghost" onClick={() => remove(p)} title={p.usedCount > 0 ? 'Tắt mã (đã có lượt dùng)' : 'Xoá'} disabled={del.isPending}>
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Mã ưu đãi"
        description="Mã giảm giá khách nhập khi mua hoặc đổi gói. Giảm sau giảm giá theo thời hạn, trước phần quy đổi gói cũ."
        actions={<Button onClick={() => setEditing('new')}><Plus /> Tạo mã</Button>}
      />

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm mã hoặc mô tả" />
      </div>

      {isError ? <ErrorState /> : (
        <DataTable columns={columns} rows={rows} rowKey={(p) => p.id} loading={isLoading} emptyTitle="Chưa có mã ưu đãi nào" />
      )}

      {editing && (
        <PromoDialog
          key={editing === 'new' ? 'new' : editing.id}
          promo={editing === 'new' ? null : editing}
          plans={plans}
          onClose={() => setEditing(null)}
        />
      )}
      <RedemptionsPanel promo={viewing} onClose={() => setViewing(null)} planName={planName} />
    </div>
  )
}

// ── Hộp thoại tạo / sửa ───────────────────────────────────────────────

function PromoDialog({ promo, plans, onClose }: {
  promo: PromoCodeItem | null
  plans: { id: string; name: string }[]
  onClose: () => void
}) {
  const [f, setF] = useState<PromoForm>(() => (promo ? promoToForm(promo) : EMPTY_PROMO_FORM))
  const [genLoading, setGenLoading] = useState(false)
  const save = useSavePromoCode()
  const set = <K extends keyof PromoForm>(k: K, v: PromoForm[K]) => setF((o) => ({ ...o, [k]: v }))
  const codeLocked = !!promo && promo.usedCount > 0
  const percent = f.discountType === 'percent'

  const gen = async () => {
    setGenLoading(true)
    try {
      set('code', await generatePromoCode())
    } catch (err) {
      toast.error(platformApiError(err))
    } finally {
      setGenLoading(false)
    }
  }

  const togglePlan = (id: string, on: boolean) =>
    set('planIds', on ? [...f.planIds, id] : f.planIds.filter((x) => x !== id))

  const submit = () => {
    const err = validatePromoForm(f)
    if (err) return toast.error(err)
    save.mutate({ id: promo?.id, input: formToInput(f) }, {
      onSuccess: (p) => {
        toast.success(promo ? `Đã lưu mã ${p.code}` : `Đã tạo mã ${p.code}`)
        onClose()
      },
      onError: (e) => toast.error(platformApiError(e)),
    })
  }

  const moneyInput = (k: 'maxDiscountAmount' | 'minAmount' | 'discountValue' | 'maxRedemptions', placeholder: string, suffix?: string) => (
    <div className="relative">
      <Input
        inputMode="numeric"
        value={f[k]}
        placeholder={placeholder}
        className={suffix ? 'pr-8' : undefined}
        onChange={(e) => set(k, formatIntInput(parseIntInput(e.target.value)))}
      />
      {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{suffix}</span>}
    </div>
  )

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{promo ? `Sửa mã ${promo.code}` : 'Tạo mã ưu đãi'}</DialogTitle>
          <DialogDescription>Để trống ô giới hạn = không giới hạn. Server kiểm lại mã mỗi lần khách báo giá và thanh toán.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="promo-code">Mã</Label>
            <div className="flex gap-2">
              <Input
                id="promo-code"
                className="font-mono uppercase"
                value={f.code}
                disabled={codeLocked}
                maxLength={32}
                placeholder="VD: TET2027"
                onChange={(e) => set('code', e.target.value.toUpperCase().replace(/\s/g, ''))}
              />
              <Button type="button" variant="outline" onClick={gen} disabled={codeLocked || genLoading} title="Sinh mã ngẫu nhiên">
                {genLoading ? <Loader2 className="animate-spin" /> : <Shuffle />} Tạo ngẫu nhiên
              </Button>
            </div>
            {codeLocked && <p className="text-xs text-muted-foreground">Mã đã có người dùng — không đổi được chuỗi mã.</p>}
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="promo-desc">Mô tả (hiện cho khách)</Label>
            <Textarea id="promo-desc" rows={2} maxLength={200} value={f.description} onChange={(e) => set('description', e.target.value)} placeholder="VD: Ưu đãi Tết — giảm 20% khi mua từ 90 ngày" />
          </div>

          <div className="space-y-1.5">
            <Label>Loại giảm</Label>
            <div className="grid grid-cols-2 gap-1 rounded-lg border p-1">
              {(['percent', 'fixed'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  className={cn('rounded-md px-3 py-1.5 text-sm transition-colors', f.discountType === t ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}
                  onClick={() => setF((o) => ({ ...o, discountType: t, discountValue: '', maxDiscountAmount: '' }))}
                >
                  {t === 'percent' ? 'Phần trăm' : 'Số tiền'}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{percent ? 'Phần trăm giảm' : 'Số tiền giảm'}</Label>
            {percent ? (
              <div className="relative">
                <Input inputMode="numeric" value={f.discountValue} placeholder="1–100" className="pr-8"
                  onChange={(e) => set('discountValue', e.target.value.replace(/\D/g, '').slice(0, 3))} />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
              </div>
            ) : moneyInput('discountValue', 'VD: 200.000', '₫')}
          </div>
          {percent && (
            <div className="space-y-1.5">
              <Label>Giảm tối đa</Label>
              {moneyInput('maxDiscountAmount', 'Không giới hạn', '₫')}
            </div>
          )}
          <div className="flex items-start gap-2 pt-6">
            <Checkbox id="promo-stack" checked={f.stackableWithDurationDiscount} onCheckedChange={(v) => set('stackableWithDurationDiscount', v === true)} />
            <Label htmlFor="promo-stack" className="font-normal leading-snug">
              Cộng dồn với giảm giá theo thời hạn
              <span className="block text-xs text-muted-foreground">Bỏ chọn: khách được mức giảm lớn hơn trong hai mức.</span>
            </Label>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label>Gói áp dụng</Label>
            <div className="flex flex-wrap gap-3 rounded-lg border p-3">
              {plans.length <= 1 && <span className="text-sm text-muted-foreground">Chưa có gói bán trực tuyến trong bảng giá</span>}
              {plans.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={f.planIds.includes(p.id)} onCheckedChange={(v) => togglePlan(p.id, v === true)} />
                  {p.name}
                </label>
              ))}
              <span className="text-xs text-muted-foreground">{f.planIds.length === 0 ? '— không chọn = mọi gói trả phí' : ''}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              “Token AI”: mã áp cho đơn mua token AI chỉ khi chọn mục này — mã “mọi gói trả phí” không áp cho token.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="promo-start">Bắt đầu</Label>
            <Input id="promo-start" type="datetime-local" value={f.startsAt} onChange={(e) => set('startsAt', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="promo-end">Kết thúc</Label>
            <Input id="promo-end" type="datetime-local" value={f.endsAt} onChange={(e) => set('endsAt', e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Số ngày mua tối thiểu</Label>
            <Input inputMode="numeric" value={f.minDays} placeholder="Không yêu cầu" onChange={(e) => set('minDays', e.target.value.replace(/\D/g, '').slice(0, 4))} />
          </div>
          <div className="space-y-1.5">
            <Label>Giá trị đơn tối thiểu</Label>
            {moneyInput('minAmount', 'Không yêu cầu', '₫')}
          </div>

          <div className="space-y-1.5">
            <Label>Tổng lượt dùng</Label>
            {moneyInput('maxRedemptions', 'Không giới hạn')}
          </div>
          <div className="space-y-1.5">
            <Label>Lượt / tổ chức</Label>
            <Input inputMode="numeric" value={f.maxPerOrg} onChange={(e) => set('maxPerOrg', e.target.value.replace(/\D/g, '').slice(0, 4))} />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={f.firstPurchaseOnly} onCheckedChange={(v) => set('firstPurchaseOnly', v === true)} />
            Chỉ cho lần mua gói đầu tiên
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={f.isActive} onCheckedChange={(v) => set('isActive', v)} />
            Đang bật
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Huỷ</Button>
          <Button onClick={submit} disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" />}
            {promo ? 'Lưu' : 'Tạo mã'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Ngăn kéo lượt dùng ────────────────────────────────────────────────

function RedemptionsPanel({ promo, onClose, planName }: {
  promo: PromoCodeItem | null
  onClose: () => void
  planName: (id: string) => string
}) {
  const { data, isLoading, isError } = usePromoRedemptions(promo?.id ?? null)
  return (
    <Dialog open={!!promo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="left-auto right-0 top-0 h-full max-h-screen w-full translate-x-0 translate-y-0 content-start rounded-none sm:max-w-xl"
      >
        <DialogHeader>
          <DialogTitle>Lượt dùng mã {promo?.code}</DialogTitle>
          <DialogDescription>
            {promo ? `${promo.usedCount} lượt · đã giảm tổng ${formatVnd(promo.totalDiscounted)}` : ''}
          </DialogDescription>
        </DialogHeader>
        {isLoading ? <Loading label="Đang tải..." /> : isError ? <ErrorState /> : !data?.length ? (
          <EmptyState title="Chưa có lượt dùng nào" />
        ) : (
          <ul className="divide-y rounded-lg border">
            {data.map((r) => (
              <li key={r.id} className="flex items-start justify-between gap-3 p-3 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium">{r.orgName}</p>
                  <p className="text-xs text-muted-foreground">
                    {dayjs(r.createdAt).format('DD/MM/YYYY HH:mm')}
                    {r.planId && ` · ${planName(r.planId)}`}
                    {r.cycle && /^d\d+$/.test(r.cycle) && ` ${r.cycle.slice(1)} ngày`}
                  </p>
                  <p className="font-mono text-xs text-muted-foreground">{r.paymentCode ?? r.orderId.slice(0, 8)}</p>
                </div>
                <div className="shrink-0 text-right tabular-nums">
                  <p className="text-success">−{formatVnd(r.amountDiscounted)}</p>
                  <p className="text-xs text-muted-foreground">Đã trả {formatVnd(r.amountPaid)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  )
}
